import React, { useState, useRef, useEffect } from 'react';
import { apiCall } from '../AuthContext';
import {
  Sparkles, Upload, Send, Loader2, X, AlertCircle, CheckCircle2, Plus, Search,
  Camera, Printer, MapPin,
} from 'lucide-react';

/**
 * Quick List — simplified single-screen listing experience for eBay / Shopify / Square.
 * Activated via the "Quick List" toggle at the top of ProductForm.
 *
 * Layout matches the user's mockup:
 *   - Big main image (left) + Add Pics / Add Video buttons
 *   - Title + Description (center)
 *   - Weights + Dimensions (right)
 *   - Brand / Color / Department / Size / Size Type / Type aspects
 *   - Pricing block with Buy It Now vs Auction toggle
 *   - eBay category auto-suggest
 *   - Big "Send To" button with channel dropdown (eBay / Shopify / Square)
 */
export default function QuickListPanel({ initial = {}, onExit }) {
  const [data, setData] = useState({
    title: initial.product_name || '',
    description: initial.product_desc || '',
    barcode: initial.barcode || '',
    sku: initial.sku || initial.item_number || '',
    price: initial.final_price || '',
    quantity: initial.quantity || 1,
    listingFormat: 'BuyItNow', // or "Auction"
    startPrice: '',
    auctionDays: '7',
    weightLbs: '', weightOz: '',
    shipWeightLbs: '', shipWeightOz: '',
    dimL: '', dimW: '', dimH: '',
    brand: '', color: '', department: '',
    size: '', sizeType: '', type: '',
    condition: 'NEW',
    images: initial.images && initial.images.length ? [...initial.images] : [],
    videoUrl: initial.video_url || '',
    ebayCategoryId: '',
    ebayCategoryLabel: '',
    // Aisle location for in-store organization. Letter (A-Z) + Number (1-99).
    // Both optional. When set, gets printed on the price-tag label.
    aisleLetter: '',
    aisleNumber: '',
    // Selected label size for "Print Tag". Matches LABEL_CANVAS keys in
    // backend/utils/labels.py. Defaults to 2x1 (price tag), but user can
    // pick 3x2 (hang tag) or 4x6 (shipping/shelf) before clicking Print.
    labelSize: '2x1',
    // "in-store" → uses the user's saved default template (store name + prices).
    // "online" → renders an inventory-style tag (huge #item-number on top,
    // product/price middle, big barcode bottom) for shipped/listed items.
    tagStyle: 'in-store',
    // Inventory # for online tags. Defaults to the SKU/barcode if left empty.
    itemNumber: initial.item_number || initial.sku || '',
    // Shopify "Physical product" toggle. ON = tangible good (default).
    // OFF = digital good / service → no shipping, no weight required.
    requiresShipping: true,
  });
  const [channel, setChannel] = useState('ebay');
  const [aiLoading, setAiLoading] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [labelPreview, setLabelPreview] = useState(null);
  const [catSuggestions, setCatSuggestions] = useState([]);
  const [showCatPicker, setShowCatPicker] = useState(false);
  const [upcLoading, setUpcLoading] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  // Saved Label Designer templates — fetched once so the size picker can show
  // them next to (and above) the basic canvas sizes. Each entry: { id, name, size }.
  const [savedTemplates, setSavedTemplates] = useState([]);
  // Selected template id (empty = use canvas-size only).
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const picsInput = useRef(null);
  const videoInput = useRef(null);
  const scannerHostRef = useRef(null);
  const scannerInstanceRef = useRef(null);

  const set = (k, v) => setData(prev => ({ ...prev, [k]: v }));

  // ── eBay category suggestions (triggered by "Show suggestions" button) ──
  const fetchCatSuggestions = async () => {
    const t = (data.title || '').trim();
    if (!t || t.length < 3) { setCatSuggestions([]); return; }
    try {
      const r = await apiCall(`/api/ebay-categories?q=${encodeURIComponent(t)}`, { method: 'GET' });
      setCatSuggestions(r.data?.categories || r.data || []);
    } catch (_) {
      setCatSuggestions([]);
    }
  };

  // ── UPC lookup → auto-fill title/description/image ──────────────
  const lookupUpc = async () => {
    const code = (data.barcode || '').trim();
    if (!code) { setError('Type or scan a UPC first.'); return; }
    setUpcLoading(true); setError('');
    try {
      const r = await apiCall(`/api/barcode-lookup?barcode=${encodeURIComponent(code)}`, { method: 'GET' });
      const found = r.data || {};
      if (!found.found) {
        setError(found.message || `No product found for UPC ${code}. Try Sam's Club lookup or fill in manually.`);
        return;
      }
      setData(prev => ({
        ...prev,
        title: prev.title || found.product_title || '',
        description: prev.description || found.product_description || '',
        images: prev.images.length ? prev.images : (found.product_images || []).slice(0, 8),
        sku: prev.sku || code,
        price: prev.price || (found.retail_price ? String(found.retail_price).replace('$', '') : prev.price),
      }));
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'UPC lookup failed');
    } finally {
      setUpcLoading(false);
    }
  };

  // ── Live camera barcode scanner (Html5Qrcode) ───────────────────
  const startScanner = async () => {
    setShowScanner(true); setError('');
    try {
      const { Html5Qrcode, Html5QrcodeSupportedFormats } = await import('html5-qrcode');
      // Wait one tick so the scanner host div is mounted.
      await new Promise(res => setTimeout(res, 80));
      if (!scannerHostRef.current) return;
      const scanner = new Html5Qrcode('quicklist-scanner', {
        // Explicitly enable 1D barcode formats. By default html5-qrcode favors
        // QR-only decoding which doesn't pick up UPC/EAN labels on phones.
        formatsToSupport: [
          Html5QrcodeSupportedFormats.UPC_A,
          Html5QrcodeSupportedFormats.UPC_E,
          Html5QrcodeSupportedFormats.EAN_13,
          Html5QrcodeSupportedFormats.EAN_8,
          Html5QrcodeSupportedFormats.CODE_128,
          Html5QrcodeSupportedFormats.CODE_39,
          Html5QrcodeSupportedFormats.ITF,
          Html5QrcodeSupportedFormats.QR_CODE,
        ],
        // Use the browser's native BarcodeDetector when available (Chrome on
        // Android) — dramatically faster + more accurate than ZXing fallback.
        useBarCodeDetectorIfSupported: true,
        verbose: false,
      });
      scannerInstanceRef.current = scanner;
      // Responsive qrbox: 80% of the shorter viewport edge, wide aspect for 1D
      // barcodes. Phones in portrait → ~240w x 120h, tablets → much bigger.
      const qrboxFn = (vw, vh) => {
        const minEdge = Math.min(vw || 300, vh || 300);
        const w = Math.floor(minEdge * 0.85);
        const h = Math.max(120, Math.floor(w * 0.4));
        return { width: w, height: h };
      };
      await scanner.start(
        { facingMode: 'environment' },
        {
          fps: 15,
          qrbox: qrboxFn,
          aspectRatio: 1.7777,
          disableFlip: false,
        },
        async (decodedText) => {
          // Got a code → stop camera, fill the field, immediately lookup
          try { await scanner.stop(); } catch (_e) { /* already stopped */ }
          scannerInstanceRef.current = null;
          set('barcode', decodedText);
          setShowScanner(false);
          // Lookup using the fresh code (avoid stale state)
          setUpcLoading(true);
          try {
            const r = await apiCall(`/api/barcode-lookup?barcode=${encodeURIComponent(decodedText)}`, { method: 'GET' });
            const found = r.data || {};
            if (found.found) {
              setData(prev => ({
                ...prev,
                barcode: decodedText,
                title: prev.title || found.product_title || '',
                description: prev.description || found.product_description || '',
                images: prev.images.length ? prev.images : (found.product_images || []).slice(0, 8),
                sku: prev.sku || decodedText,
                price: prev.price || (found.retail_price ? String(found.retail_price).replace('$', '') : prev.price),
              }));
            } else {
              setError(`Scanned ${decodedText} but no product info found. Fill in manually.`);
            }
          } catch (lookupErr) {
            setError(lookupErr.response?.data?.detail || 'Lookup after scan failed');
          } finally {
            setUpcLoading(false);
          }
        },
        () => {/* per-frame decode errors are noisy — ignore */},
      );
    } catch (e) {
      setShowScanner(false);
      setError('Could not start camera. Make sure your browser has camera access for this site (Settings → Site Settings → Camera).');
    }
  };

  const stopScanner = () => {
    if (scannerInstanceRef.current) {
      scannerInstanceRef.current.stop().catch(() => {});
      scannerInstanceRef.current = null;
    }
    setShowScanner(false);
  };

  useEffect(() => {
    // Cleanup camera on unmount
    return () => {
      if (scannerInstanceRef.current) {
        try { scannerInstanceRef.current.stop(); } catch (_e) { /* already stopped */ }
        scannerInstanceRef.current = null;
      }
    };
  }, []);

  // Load saved Label Designer templates once on mount so the size picker can
  // surface them. Cheap call; cached by the browser between Quick List opens.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiCall('/api/label-templates', { method: 'GET' });
        if (cancelled) return;
        // Endpoint returns either a raw list or { templates: [...] } depending on version
        const raw = Array.isArray(r.data) ? r.data : (r.data?.templates || []);
        const tpls = raw.map(t => ({
          id: t.id, name: t.name || 'Untitled', size: t.size || '',
          is_default: !!t.is_default,
        }));
        setSavedTemplates(tpls);
        // Auto-select the user's default template if any (matches the canvas
        // size in the dropdown so Print uses it without a click).
        const def = tpls.find(t => t.is_default);
        if (def) setSelectedTemplateId(def.id);
      } catch (_e) { /* user might not have any templates yet */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Auto-generate an inventory item # on first mount (if none provided).
  // Runs once per panel load so the user always gets a fresh sequential
  // number without having to click anything.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (data.itemNumber) return;
      try {
        const r = await apiCall('/api/quick-list/next-item-number', { method: 'POST', data: {} });
        if (!cancelled && r.data?.item_number) {
          setData(prev => prev.itemNumber ? prev : { ...prev, itemNumber: r.data.item_number });
        }
      } catch (_e) { /* fallback: user can type one manually */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Print price-tag label (includes Aisle if set) ───────────────
  const printLabel = async () => {
    if (!data.title.trim() && !data.barcode.trim()) {
      setError('Need at least a title or barcode to print a label.');
      return;
    }
    setPrinting(true); setError('');
    try {
      const aisle = `${(data.aisleLetter || '').toUpperCase()}${data.aisleNumber || ''}`.trim();
      const r = await apiCall('/api/print-label', {
        method: 'POST',
        data: {
          product_name: data.title || 'Untitled',
          retail_price: String(data.price || '0'),
          our_price: String(data.price || '0'),
          barcode: data.barcode || '000000000000',
          aisle,
          size: data.labelSize || (data.tagStyle === 'online' ? '4x6' : '2x1'),
          style: data.tagStyle,
          // Online tags use this as the giant # at the top
          item_number: data.itemNumber || data.sku || data.barcode || '',
          // When the user picked a saved Label Designer template, send its ID
          // so the backend renders that exact ZPL layout (ignoring style/size).
          template_id: selectedTemplateId || '',
        },
      });
      if (r.data?.error) {
        setError(r.data.message || 'Label printing failed');
        return;
      }
      setLabelPreview({ image: r.data.image, aisle });
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Print failed');
    } finally {
      setPrinting(false);
    }
  };

  const printLabelNow = () => {
    if (!labelPreview?.image) return;
    const w = window.open('', '_blank', 'width=600,height=800');
    if (!w) { setError('Browser blocked the print window. Allow pop-ups.'); return; }
    w.document.write(`<!DOCTYPE html><html><head><title>Label</title>
      <style>@page{margin:0}html,body{margin:0;padding:0}img{width:100%;display:block}</style>
      </head><body><img src="${labelPreview.image}" /></body></html>`);
    w.document.close();
    setTimeout(() => { try { w.focus(); w.print(); } catch (_e) { /* popup may be blocked */ } }, 400);
  };

  // ── Smart Fill — GPT-5.2 fills brand/color/department/etc. ──────
  const smartFill = async () => {
    if (!data.title.trim()) { setError('Add a product title first so AI knows what to extract.'); return; }
    setAiLoading(true); setError('');
    try {
      const r = await apiCall('/api/quick-list/smart-fill', {
        method: 'POST',
        data: {
          title: data.title,
          description: data.description,
          image_url: data.images?.[0] || '',
        },
      });
      const a = r.data || {};
      setData(prev => ({
        ...prev,
        brand: a.brand || prev.brand,
        color: a.color || prev.color,
        department: a.department || prev.department,
        size: a.size || prev.size,
        sizeType: a.size_type || prev.sizeType,
        type: a.type || prev.type,
        description: a.cleaned_description || prev.description,
      }));
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Smart Fill failed');
    } finally {
      setAiLoading(false);
    }
  };

  // ── Image upload (multi-file) ────────────────────────────────────
  const onPicsSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setError('');
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      const r = await apiCall('/api/upload-product-images', {
        method: 'POST', data: fd,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const urls = r.data?.urls || r.data?.image_urls || [];
      setData(prev => ({ ...prev, images: [...prev.images, ...urls].slice(0, 12) }));
    } catch (e) {
      setError(e.response?.data?.detail || 'Image upload failed');
    } finally {
      if (picsInput.current) picsInput.current.value = '';
    }
  };

  const onVideoSelected = async (e) => {
    const f = (e.target.files || [])[0];
    if (!f) return;
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await apiCall('/api/upload-video', {
        method: 'POST', data: fd,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      set('videoUrl', r.data?.url || r.data?.video_url || '');
    } catch (e) {
      setError(e.response?.data?.detail || 'Video upload failed');
    } finally {
      if (videoInput.current) videoInput.current.value = '';
    }
  };

  // ── Send to selected channel ────────────────────────────────────
  const send = async () => {
    setError(''); setSuccess(null);
    if (!data.title.trim()) { setError('Title is required.'); return; }
    // SKU priority for marketplace pushes: explicit SKU > inventory # > barcode.
    // The inventory # is used as the eBay/Shopify/Square SKU so it lines up with
    // the printed online tag and the user's internal inventory ledger.
    const effectiveSku = (data.sku || data.itemNumber || data.barcode || '').trim();
    setPushing(true);
    try {
      if (channel === 'ebay') {
        if (!data.ebayCategoryId) { setPushing(false); setError('Pick an eBay category from the suggestions.'); return; }
        const r = await apiCall('/api/quick-list/push-ebay', {
          method: 'POST',
          data: {
            productName: data.title, Desc: data.description, barCode: data.barcode,
            finalPrice: String(data.price || 0), quantity: Number(data.quantity || 1),
            sku: effectiveSku, categoryId: data.ebayCategoryId, condition: data.condition,
            imageUrls: data.images, weightLbs: data.weightLbs, weightOz: data.weightOz,
            dimLength: data.dimL, dimWidth: data.dimW, dimHeight: data.dimH,
            brand: data.brand, color: data.color, department: data.department,
            size: data.size, size_type: data.sizeType, itemType: data.type,
            listingFormat: data.listingFormat,
            startPrice: data.startPrice, auctionDays: data.auctionDays,
          },
        });
        setSuccess({ channel: 'eBay', msg: r.data?.message || 'Listed on eBay', listingId: r.data?.listing_id });
      } else if (channel === 'shopify') {
        const r = await apiCall('/api/push-shopify', {
          method: 'POST',
          data: {
            barCode: data.barcode, productName: data.title, Desc: data.description,
            finalPrice: String(data.price || 0), quantity: String(data.quantity || 1),
            sku: effectiveSku, squareImageUrl: data.images,
            productType: data.type || '', vendor: data.brand || '', weight: data.weightLbs ? Number(data.weightLbs) : 0,
            requiresShipping: data.requiresShipping,
            salesChannels: [],
          },
        });
        setSuccess({ channel: 'Shopify', msg: r.data?.message || 'Listed on Shopify' });
      } else if (channel === 'square') {
        const r = await apiCall('/api/push-square', {
          method: 'POST',
          data: {
            barCode: data.barcode, productName: data.title, Desc: data.description,
            finalPrice: String(data.price || 0), retailPrice: String(data.price || 0),
            quantity: String(data.quantity || 1), squareImageUrl: data.images,
            availableOnline: data.images.length > 0, sku: effectiveSku,
            categoryName: data.type || '',
          },
        });
        setSuccess({ channel: 'Square', msg: r.data?.message || 'Listed on Square' });
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message || `${channel} push failed`);
    } finally {
      setPushing(false);
    }
  };

  const cta = channel === 'ebay' ? 'List on eBay' : channel === 'shopify' ? 'List on Shopify' : 'List on Square';
  const accent = channel === 'ebay' ? '#0064D2' : channel === 'shopify' ? '#5E8E3E' : '#000000';

  return (
    <div className="min-h-screen bg-slate-50 quick-list-kiosk" data-testid="quick-list-panel">
      <style>{`
        /* Tablet kiosk mode: larger touch targets + breathing room.
           Active between 768px (md) and 1366px (covers iPads landscape). */
        @media (min-width: 768px) and (max-width: 1366px) {
          .quick-list-kiosk input,
          .quick-list-kiosk select,
          .quick-list-kiosk textarea { font-size: 16px !important; padding-top: .55rem !important; padding-bottom: .55rem !important; }
          .quick-list-kiosk button { min-height: 44px; }
          .quick-list-kiosk .kiosk-card { padding: 1rem !important; }
          .quick-list-kiosk .kiosk-label { font-size: 12px !important; }
          .quick-list-kiosk .kiosk-title-bar { padding-top: .75rem; padding-bottom: .75rem; }
          .quick-list-kiosk .kiosk-image { aspect-ratio: 1 / 1; }
        }
        /* Make the whole experience feel full-screen (no body scrollbar gutter) */
        body:has(.quick-list-kiosk) { overflow-x: hidden; }
      `}</style>
      {/* Sticky top bar — mobile-friendly */}
      <div className="sticky top-0 z-30 bg-white border-b border-slate-200 shadow-sm kiosk-title-bar">
        <div className="max-w-6xl mx-auto px-3 md:px-6 py-2 md:py-3 flex items-center gap-2 md:gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 leading-tight">Quick List</div>
            <div className="text-sm sm:text-base font-bold text-slate-900 truncate leading-tight" data-testid="quick-list-title">
              {data.title || 'New listing'}
            </div>
          </div>
          <select value={channel} onChange={e => setChannel(e.target.value)}
            className="px-2 py-1.5 rounded-md bg-slate-100 border border-slate-300 text-xs font-semibold text-slate-800"
            data-testid="ql-channel-select">
            <option value="ebay">eBay</option>
            <option value="shopify">Shopify</option>
            <option value="square">Square</option>
          </select>
          <select value={data.tagStyle} onChange={e => set('tagStyle', e.target.value)}
            className="px-2 py-1.5 rounded-md bg-white border border-slate-300 text-xs font-semibold text-slate-800 hidden md:inline-block"
            title="In-Store uses your saved template; Online prints a big #item-number tag"
            data-testid="ql-tag-style-select">
            <option value="in-store">In-Store</option>
            <option value="online">Online</option>
          </select>
          <select
            value={selectedTemplateId || `__size:${data.labelSize}`}
            onChange={e => {
              const v = e.target.value;
              if (v.startsWith('__size:')) {
                setSelectedTemplateId('');
                set('labelSize', v.replace('__size:', ''));
              } else {
                setSelectedTemplateId(v);
                const tpl = savedTemplates.find(t => t.id === v);
                if (tpl?.size) set('labelSize', tpl.size);
              }
            }}
            className="px-2 py-1.5 rounded-md bg-white border border-slate-300 text-xs font-semibold text-slate-800 hidden md:inline-block max-w-[180px]"
            title="Label template or canvas size for Print Tag"
            data-testid="ql-label-size-select">
            {savedTemplates.length > 0 && (
              <optgroup label="My Templates">
                {savedTemplates.map(t => (
                  <option key={t.id} value={t.id}>{t.name}{t.is_default ? ' ★' : ''}{t.size ? ` (${t.size})` : ''}</option>
                ))}
              </optgroup>
            )}
            <optgroup label="Canvas Size">
              <option value="__size:2x1">2&quot; × 1&quot;</option>
              <option value="__size:3x2">3&quot; × 2&quot;</option>
              <option value="__size:4x6">4&quot; × 6&quot;</option>
            </optgroup>
          </select>
          <button onClick={printLabel} disabled={printing}
            className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-md px-3 py-1.5 text-sm shadow-sm disabled:opacity-60"
            title="Print price tag (includes Aisle if set)"
            data-testid="ql-print-btn">
            {printing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
            <span className="hidden sm:inline">{printing ? 'Printing…' : 'Print Tag'}</span>
          </button>
          <button onClick={send} disabled={pushing}
            style={{ backgroundColor: accent }}
            className="inline-flex items-center gap-1.5 text-white font-semibold rounded-md px-3 py-1.5 text-sm shadow-sm disabled:opacity-60 hover:opacity-90 transition-opacity"
            data-testid="ql-send-btn">
            {pushing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            <span className="hidden sm:inline">{pushing ? 'Sending…' : cta}</span>
          </button>
          {onExit && (
            <button onClick={onExit} className="p-1.5 rounded-md hover:bg-slate-100" title="Close" data-testid="ql-exit-btn">
              <X className="w-4 h-4 text-slate-700" />
            </button>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-3 md:px-6 py-3 md:py-5 space-y-3 md:space-y-4 pb-24 sm:pb-3">
        {error && (
          <div className="px-3 py-1.5 rounded-md bg-red-50 border border-red-200 text-red-800 text-xs flex items-start gap-2" data-testid="ql-error">
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> <span>{error}</span>
          </div>
        )}
        {success && (
          <div className="px-3 py-1.5 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs flex items-start gap-2" data-testid="ql-success">
            <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>
              <strong>{success.channel}:</strong> {success.msg}
              {success.listingId && <a href={`https://www.ebay.com/itm/${success.listingId}`} target="_blank" rel="noreferrer" className="ml-2 underline font-semibold">View →</a>}
            </span>
          </div>
        )}

        {/* ─── COMPACT TOP ROW: Images | Title+Desc | Weights+Dims ─── */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          {/* LEFT: image preview + add pics/video */}
          <div className="md:col-span-3 bg-white rounded-lg border border-slate-200 shadow-sm p-2.5 kiosk-card">
            <div className="aspect-square bg-slate-100 rounded-md overflow-hidden flex items-center justify-center kiosk-image" data-testid="ql-image-preview">
              {data.images[0] ? (
                <img src={data.images[0]} alt="" className="w-full h-full object-contain" />
              ) : (
                <button onClick={() => picsInput.current?.click()} className="w-full h-full flex flex-col items-center justify-center gap-1 text-slate-400 hover:bg-slate-200 transition-colors" data-testid="ql-add-pics-empty">
                  <Upload className="w-6 h-6" />
                  <span className="text-[11px] font-semibold">Tap to add photos</span>
                </button>
              )}
            </div>
            {data.images.length > 0 && (
              <div className="flex gap-1 mt-1.5 overflow-x-auto" data-testid="ql-thumbs">
                {data.images.map((u, i) => (
                  <div key={i} className={`relative shrink-0 ${i === 0 ? 'ring-2 ring-blue-500' : ''} rounded`}>
                    <img src={u} alt="" className="w-9 h-9 object-cover rounded" />
                    <button onClick={() => set('images', data.images.filter((_, j) => j !== i))}
                      className="absolute -top-1 -right-1 bg-red-500 hover:bg-red-600 text-white rounded-full w-4 h-4 text-[9px] flex items-center justify-center shadow"
                      data-testid={`ql-img-remove-${i}`}>×</button>
                  </div>
                ))}
                <button onClick={() => picsInput.current?.click()}
                  className="shrink-0 w-9 h-9 rounded border border-dashed border-slate-300 hover:border-blue-400 flex items-center justify-center text-slate-400"
                  data-testid="ql-add-more-pics">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            )}
            <div className="grid grid-cols-2 gap-1.5 mt-1.5">
              <button onClick={() => picsInput.current?.click()}
                className="px-2 py-1.5 rounded-md bg-slate-100 hover:bg-slate-200 text-[11px] font-semibold text-slate-800 inline-flex items-center justify-center gap-1"
                data-testid="ql-add-pics-btn">
                <Upload className="w-3.5 h-3.5" /> Photos
              </button>
              <button onClick={() => videoInput.current?.click()}
                className="px-2 py-1.5 rounded-md bg-slate-100 hover:bg-slate-200 text-[11px] font-semibold text-slate-800 inline-flex items-center justify-center gap-1"
                data-testid="ql-add-video-btn">
                <Upload className="w-3.5 h-3.5" /> Video
              </button>
            </div>
            <input ref={picsInput} type="file" multiple accept="image/*" capture="environment" className="hidden" onChange={onPicsSelected} />
            <input ref={videoInput} type="file" accept="video/*" capture="environment" className="hidden" onChange={onVideoSelected} />
            {data.videoUrl && (
              <div className="mt-1.5 text-[10px] text-emerald-700 font-semibold flex items-center gap-1" data-testid="ql-video-attached">
                <CheckCircle2 className="w-3 h-3" /> Video attached
              </div>
            )}
          </div>

          {/* CENTER: UPC + Title + Description (taller) */}
          <div className="md:col-span-6 bg-white rounded-lg border border-slate-200 shadow-sm p-2.5 kiosk-card space-y-2">
            <div className="flex gap-1.5">
              <input value={data.barcode} onChange={e => set('barcode', e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); lookupUpc(); } }}
                placeholder="Scan or type UPC"
                inputMode="numeric"
                className="flex-1 px-2.5 py-1.5 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="ql-upc-input" />
              <button onClick={() => (showScanner ? stopScanner() : startScanner())}
                className={`px-2 py-1.5 rounded-md text-xs font-semibold inline-flex items-center gap-1 ${showScanner ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
                title={showScanner ? 'Stop camera scanner' : 'Scan a barcode using your tablet/phone camera'}
                data-testid="ql-scan-btn">
                {showScanner ? <X className="w-3.5 h-3.5" /> : <Camera className="w-3.5 h-3.5" />}
                <span className="hidden sm:inline">{showScanner ? 'Stop' : 'Scan'}</span>
              </button>
              <button onClick={lookupUpc} disabled={upcLoading}
                className="px-2 py-1.5 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold disabled:opacity-60 inline-flex items-center gap-1"
                data-testid="ql-upc-lookup-btn">
                {upcLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                <span className="hidden sm:inline">Lookup</span>
              </button>
              <button onClick={smartFill} disabled={aiLoading}
                className="px-2 py-1.5 rounded-md bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white text-xs font-bold disabled:opacity-60 inline-flex items-center gap-1 shadow-sm"
                title="Auto-fill brand, color, size, description with GPT-5.2"
                data-testid="ql-smart-fill-btn">
                {aiLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                Smart Fill
              </button>
            </div>
            {showScanner && (
              <div className="rounded-md overflow-hidden border border-slate-300 bg-black" data-testid="ql-scanner-host">
                <div id="quicklist-scanner" ref={scannerHostRef} style={{ width: '100%' }} />
                <p className="text-center text-[11px] text-white py-1">Point camera at a UPC barcode</p>
              </div>
            )}
            <input value={data.title} onChange={e => set('title', e.target.value)}
              className="w-full px-2.5 py-1.5 rounded-md border border-slate-300 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Title — e.g. Nike Sport Shorts Size XL — Orange"
              data-testid="ql-title-input" />
            <textarea value={data.description} onChange={e => set('description', e.target.value)}
              rows={7}
              className="w-full px-2.5 py-1.5 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              placeholder={"Product description (bullets work great):\n- Nike Sport Shorts\n- Size XL\n- Color Orange"}
              data-testid="ql-desc-input" />
            <div className="text-[10px] text-slate-400 text-right">{(data.title || '').length}/80 chars</div>
          </div>

          {/* RIGHT: weights + dimensions (compact) */}
          <div className="md:col-span-3 bg-white rounded-lg border border-slate-200 shadow-sm p-2.5 kiosk-card space-y-2">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1">Item Weight</label>
              <div className="flex items-center gap-1">
                <input value={data.weightLbs} onChange={e => set('weightLbs', e.target.value)} inputMode="decimal"
                  className="flex-1 min-w-0 px-1.5 py-1 rounded border border-slate-300 text-xs" placeholder="0" data-testid="ql-weight-lbs" />
                <span className="text-[10px] text-slate-600 font-semibold">Lbs</span>
                <input value={data.weightOz} onChange={e => set('weightOz', e.target.value)} inputMode="decimal"
                  className="flex-1 min-w-0 px-1.5 py-1 rounded border border-slate-300 text-xs" placeholder="0" data-testid="ql-weight-oz" />
                <span className="text-[10px] text-slate-600 font-semibold">Oz</span>
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1">Ship Weight</label>
              <div className="flex items-center gap-1">
                <input value={data.shipWeightLbs} onChange={e => set('shipWeightLbs', e.target.value)} inputMode="decimal"
                  className="flex-1 min-w-0 px-1.5 py-1 rounded border border-slate-300 text-xs" placeholder="0" data-testid="ql-shipweight-lbs" />
                <span className="text-[10px] text-slate-600 font-semibold">Lbs</span>
                <input value={data.shipWeightOz} onChange={e => set('shipWeightOz', e.target.value)} inputMode="decimal"
                  className="flex-1 min-w-0 px-1.5 py-1 rounded border border-slate-300 text-xs" placeholder="0" data-testid="ql-shipweight-oz" />
                <span className="text-[10px] text-slate-600 font-semibold">Oz</span>
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1">Dimensions (in)</label>
              <div className="grid grid-cols-3 gap-1">
                <div>
                  <input value={data.dimL} onChange={e => set('dimL', e.target.value)} inputMode="decimal"
                    className="w-full px-1.5 py-1 rounded border border-slate-300 text-xs text-center" placeholder="L" data-testid="ql-dim-l" />
                  <div className="text-[9px] text-slate-500 text-center mt-0.5">L</div>
                </div>
                <div>
                  <input value={data.dimW} onChange={e => set('dimW', e.target.value)} inputMode="decimal"
                    className="w-full px-1.5 py-1 rounded border border-slate-300 text-xs text-center" placeholder="W" data-testid="ql-dim-w" />
                  <div className="text-[9px] text-slate-500 text-center mt-0.5">W</div>
                </div>
                <div>
                  <input value={data.dimH} onChange={e => set('dimH', e.target.value)} inputMode="decimal"
                    className="w-full px-1.5 py-1 rounded border border-slate-300 text-xs text-center" placeholder="H" data-testid="ql-dim-h" />
                  <div className="text-[9px] text-slate-500 text-center mt-0.5">H</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ─── ASPECTS row: Brand / Color / Department / Size / Size Type / Type + Aisle ─── */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-2.5">
          <div className="grid grid-cols-2 md:grid-cols-8 gap-2">
            {[
              ['brand', 'Brand', 'Nike'],
              ['color', 'Color', 'Orange'],
              ['department', 'Department', 'Women'],
              ['size', 'Size', 'XL'],
              ['sizeType', 'Size Type', 'Regular'],
              ['type', 'Type', 'Shorts'],
            ].map(([k, label, ph]) => (
              <div key={k}>
                <label className="block text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1">{label}</label>
                <input value={data[k]} onChange={e => set(k, e.target.value)}
                  placeholder={ph}
                  className="w-full px-2 py-1.5 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid={`ql-aspect-${k}`} />
              </div>
            ))}
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1 inline-flex items-center gap-1">
                <MapPin className="w-2.5 h-2.5" /> Aisle Letter
              </label>
              <select value={data.aisleLetter} onChange={e => set('aisleLetter', e.target.value)}
                className="w-full px-2 py-1.5 rounded-md border border-slate-300 text-sm bg-white font-semibold"
                data-testid="ql-aisle-letter">
                <option value="">—</option>
                {'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map(L => <option key={L} value={L}>{L}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1">Aisle Number</label>
              <input value={data.aisleNumber} onChange={e => set('aisleNumber', e.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
                inputMode="numeric"
                placeholder="1-99"
                className="w-full px-2 py-1.5 rounded-md border border-slate-300 text-sm font-semibold"
                data-testid="ql-aisle-number" />
            </div>
          </div>
        </div>

        {/* ─── Pricing + eBay options in one row ─── */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-2.5">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1">Price (USD)</label>
              <input value={data.price} onChange={e => set('price', e.target.value)} inputMode="decimal"
                placeholder="29.99"
                className="w-full px-2 py-1.5 rounded-md border border-slate-300 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="ql-price" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1">Qty</label>
              <input value={data.quantity} onChange={e => set('quantity', e.target.value)} inputMode="numeric"
                className="w-full px-2 py-1.5 rounded-md border border-slate-300 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="ql-quantity" />
            </div>
            {data.tagStyle === 'online' && (
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1">Item # (for tag)</label>
                <input value={data.itemNumber} onChange={e => set('itemNumber', e.target.value)}
                  placeholder="10228"
                  className="w-full px-2 py-1.5 rounded-md border border-slate-300 text-sm font-mono font-bold focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="ql-item-number" />
              </div>
            )}
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1">Physical?</label>
              <label className="flex items-center gap-2 px-2 py-1.5 rounded-md border border-slate-300 bg-white cursor-pointer">
                <input type="checkbox" checked={data.requiresShipping}
                  onChange={e => set('requiresShipping', e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  data-testid="ql-physical-toggle" />
                <span className="text-xs font-semibold text-slate-700">{data.requiresShipping ? 'Yes — ships' : 'Digital / Service'}</span>
              </label>
            </div>
            {channel === 'ebay' ? (
              <>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1">Format</label>
                  <select value={data.listingFormat} onChange={e => set('listingFormat', e.target.value)}
                    className="w-full px-2 py-1.5 rounded-md border border-slate-300 text-sm bg-white" data-testid="ql-listing-format">
                    <option value="BuyItNow">Buy It Now</option>
                    <option value="Auction">Auction</option>
                  </select>
                </div>
                {data.listingFormat === 'Auction' ? (
                  <>
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1">Start Bid</label>
                      <input value={data.startPrice} onChange={e => set('startPrice', e.target.value)} inputMode="decimal"
                        placeholder="0.99"
                        className="w-full px-2 py-1.5 rounded-md border border-slate-300 text-sm" data-testid="ql-start-price" />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1">Duration</label>
                      <select value={data.auctionDays} onChange={e => set('auctionDays', e.target.value)}
                        className="w-full px-2 py-1.5 rounded-md border border-slate-300 text-sm bg-white" data-testid="ql-auction-days">
                        {['1', '3', '5', '7', '10'].map(d => <option key={d} value={d}>{d}d</option>)}
                      </select>
                    </div>
                  </>
                ) : (
                  <div className="col-span-2 md:col-span-2">
                    <label className="block text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1">Condition</label>
                    <select value={data.condition} onChange={e => set('condition', e.target.value)}
                      className="w-full px-2 py-1.5 rounded-md border border-slate-300 text-sm bg-white" data-testid="ql-condition">
                      <option value="NEW">New</option>
                      <option value="NEW_OTHER">New (Other)</option>
                      <option value="USED_EXCELLENT">Used – Excellent</option>
                      <option value="USED_GOOD">Used – Good</option>
                      <option value="USED_ACCEPTABLE">Used – Acceptable</option>
                      <option value="REFURBISHED">Refurbished</option>
                    </select>
                  </div>
                )}
                <div className="col-span-2 md:col-span-2">
                  <label className="block text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1">eBay Category</label>
                  <div className="flex gap-1">
                    <input value={data.ebayCategoryLabel}
                      onChange={e => set('ebayCategoryLabel', e.target.value)}
                      placeholder="Tap → for suggestions"
                      className="flex-1 min-w-0 px-2 py-1.5 rounded-md border border-slate-300 text-sm" data-testid="ql-cat-label" />
                    <input value={data.ebayCategoryId} onChange={e => set('ebayCategoryId', e.target.value)}
                      placeholder="ID" className="w-14 px-1.5 py-1.5 rounded-md border border-slate-300 text-sm font-mono"
                      data-testid="ql-cat-id" />
                    <button onClick={() => { setShowCatPicker(s => { const next = !s; if (next) fetchCatSuggestions(); return next; }); }}
                      className="px-2 py-1.5 rounded-md bg-slate-100 hover:bg-slate-200 text-xs font-bold text-slate-700"
                      data-testid="ql-cat-toggle">→</button>
                  </div>
                  {showCatPicker && (
                    <div className="mt-1 max-h-40 overflow-y-auto rounded-md border border-slate-200 bg-slate-50" data-testid="ql-cat-suggestions">
                      {catSuggestions.length === 0 && (
                        <div className="p-2 text-[11px] text-slate-500">Type a title above, then tap → for suggestions, or enter an eBay Category ID.</div>
                      )}
                      {catSuggestions.map((c, i) => (
                        <button key={i}
                          onClick={() => { set('ebayCategoryId', String(c.categoryId || c.id || '')); set('ebayCategoryLabel', c.categoryName || c.name || ''); setShowCatPicker(false); }}
                          className="block w-full text-left px-2 py-1.5 hover:bg-white text-xs border-b border-slate-200 last:border-b-0"
                          data-testid={`ql-cat-opt-${i}`}>
                          <span className="font-semibold text-slate-800">{c.categoryName || c.name}</span>
                          {(c.categoryId || c.id) && <span className="text-slate-500 ml-1 text-[10px]">#{c.categoryId || c.id}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="col-span-2 md:col-span-4 text-[11px] text-slate-500 self-end pb-1">
                {channel === 'shopify' ? 'Shopify will use the Title as Product Type unless you set one in the form. SKU & barcode are auto-set.' : 'Square will auto-create a category from the Type field if it doesn\'t exist.'}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sticky bottom CTA on mobile */}
      <div className="sm:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-slate-200 px-3 py-2.5 shadow-lg space-y-2">
        <div className="flex gap-2 items-center">
          <select value={data.tagStyle} onChange={e => set('tagStyle', e.target.value)}
            className="px-2 py-2 rounded-md bg-slate-100 border border-slate-300 text-xs font-semibold text-slate-800"
            title="Tag style"
            data-testid="ql-tag-style-mobile">
            <option value="in-store">Store</option>
            <option value="online">Online</option>
          </select>
          <select
            value={selectedTemplateId || `__size:${data.labelSize}`}
            onChange={e => {
              const v = e.target.value;
              if (v.startsWith('__size:')) {
                setSelectedTemplateId('');
                set('labelSize', v.replace('__size:', ''));
              } else {
                setSelectedTemplateId(v);
                const tpl = savedTemplates.find(t => t.id === v);
                if (tpl?.size) set('labelSize', tpl.size);
              }
            }}
            className="px-2 py-2 rounded-md bg-slate-100 border border-slate-300 text-xs font-semibold text-slate-800 max-w-[110px]"
            title="Template or size"
            data-testid="ql-label-size-mobile">
            {savedTemplates.length > 0 && (
              <optgroup label="Templates">
                {savedTemplates.map(t => (
                  <option key={t.id} value={t.id}>{t.name.slice(0, 14)}{t.is_default ? '★' : ''}</option>
                ))}
              </optgroup>
            )}
            <optgroup label="Canvas">
              <option value="__size:2x1">2×1</option>
              <option value="__size:3x2">3×2</option>
              <option value="__size:4x6">4×6</option>
            </optgroup>
          </select>
          <button onClick={printLabel} disabled={printing}
            className="inline-flex items-center justify-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-lg px-3 py-2.5 text-sm shadow-sm disabled:opacity-60"
            data-testid="ql-print-btn-mobile">
            {printing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
            Print
          </button>
          <button onClick={send} disabled={pushing}
            style={{ backgroundColor: accent }}
            className="flex-1 inline-flex items-center justify-center gap-2 text-white font-bold rounded-lg py-2.5 text-sm shadow-sm disabled:opacity-60"
            data-testid="ql-send-btn-mobile">
            {pushing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            {pushing ? 'Sending…' : cta}
          </button>
        </div>
      </div>

      {/* Label preview modal — shown after printLabel() returns a PNG */}
      {labelPreview && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setLabelPreview(null)} data-testid="ql-label-modal">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Label Preview</div>
                {labelPreview.aisle && (
                  <div className="text-sm font-semibold text-slate-900 mt-0.5 inline-flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5 text-blue-600" /> Aisle {labelPreview.aisle}
                  </div>
                )}
              </div>
              <button onClick={() => setLabelPreview(null)} className="p-1.5 rounded hover:bg-slate-100">
                <X className="w-4 h-4 text-slate-700" />
              </button>
            </div>
            <div className="bg-slate-100 rounded-lg p-2 mb-3 flex items-center justify-center">
              <img src={labelPreview.image} alt="Label" className="max-h-[60vh] w-auto object-contain" data-testid="ql-label-img" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setLabelPreview(null)}
                className="px-3 py-2.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-sm font-semibold text-slate-800">Close</button>
              <button onClick={printLabelNow}
                className="px-3 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold inline-flex items-center justify-center gap-1.5"
                data-testid="ql-label-print-now">
                <Printer className="w-4 h-4" /> Print Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
